<template>
  <div data-component="app-dialer-transfer-content">
    <app-dialer-tabs
      v-if="tabsWithAgentsTab && tabsWithAgentsTab.length > 1"
      :tabs="tabsWithAgentsTab"
      :active-tab-id="activeTabId"
      :type="tabsType"
      :size="size"
      @tab-change="activeTabId = $event"
    />

    <app-dialer-agent-list
      v-if="showAgentsTab && activeTabId === AGENTS_TAB_ID"
      :avatar="avatar"
      :agents="agents"
      :rule-groups="ruleGroups"
      :size="size"
      :actions-type="actionsType"
      :agent-actions-type="agentActionsType"
      :statuses="statuses"
      :default-search-options="defaultSearchOptions"
      :can-change-search-options="canChangeSearchOptions"
      :is-loading="areAgentsLoading"
      :is-disabled="areAgentActionsDisabled"
      :can-invite-third-party="canInviteThirdParty"
      @invite-agent="$emit('invite-agent', $event)"
      @invite-3rd-party="$emit('invite-3rd-party', $event)"
    />

    <app-dialer-custom-list
      v-if="activeTabId !== AGENTS_TAB_ID"
      :code="activeTab ? activeTab.code : ''"
      :context="context"
      :size="size"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue-demi';

import AppDialerAgentList from './AppDialerAgentList.vue';
import AppDialerCustomList from './AppDialerCustomList.vue';
import { AppDialerTabs } from '../common';
import { PopupTab } from '../../types';
import { IAgent } from '@/types';
import {
  AVATAR_PROP,
  RULE_GROUPS_PROP,
  SIZE_PROP,
  TABS_DISPLAY_PROP,
  LIST_ITEM_ACTIONS_DISPLAY_PROP,
  AGENT_ACTIONS_TYPE_PROP,
} from '../../constants';

export default defineComponent({
  name: 'AppDialerTransferContent',
  components: {
    AppDialerTabs,
    AppDialerAgentList,
    AppDialerCustomList,
  },
  props: {
    avatar: AVATAR_PROP,
    agents: {
      type: Array as PropType<IAgent[]>,
      default: () => [],
    },
    tabs: {
      type: Array as PropType<PopupTab[]>,
      default: () => [],
    },
    context: {
      type: Object,
      default: () => ({}),
    },
    ruleGroups: RULE_GROUPS_PROP,
    tabsType: TABS_DISPLAY_PROP,
    /** Applied only to agents tab, custom tabs are auto-defined */
    actionsType: LIST_ITEM_ACTIONS_DISPLAY_PROP,
    agentActionsType: AGENT_ACTIONS_TYPE_PROP,
    size: SIZE_PROP,
    showAgentsTab: Boolean,
    statuses: {
      type: Array,
      default: () => [],
    },
    defaultSearchOptions: {
      type: Object,
      default: undefined,
    },
    canChangeSearchOptions: Boolean,
    areAgentsLoading: Boolean,
    areAgentActionsDisabled: Boolean,
    canInviteThirdParty: Boolean,
  },
  emits: ['invite-agent', 'invite-3rd-party'],
  data() {
    return {
      activeTabId: this.showAgentsTab
        ? 'agents'
        : this.tabs.length
          ? this.tabs[0].key || '0'
          : '',
      AGENTS_TAB_ID: 'agents',
    };
  },
  computed: {
    tabsWithAgentsTab() {
      const AGENTS_TAB: PopupTab = {
        key: this.AGENTS_TAB_ID,
        label: this.$t('hitl.modules.dialer.agent-list-tab-title'),
      };

      return this.showAgentsTab ? [AGENTS_TAB, ...this.tabs] : this.tabs;
    },

    activeTab() {
      return this.tabsWithAgentsTab.find((tab, index) => {
        const matchIndex = index.toString() === this.activeTabId;
        const matchKey = tab.key === this.activeTabId;
        return matchIndex || matchKey;
      });
    },
  },
  watch: {
    tabs() {
      this.activeTabId = this.showAgentsTab
        ? this.AGENTS_TAB_ID
        : this.tabs.length
          ? this.tabs[0].key || '0'
          : '';
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='app-dialer-transfer-content'] {
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .app-dialer-tabs {
    flex: 0 0 auto;
    margin: 0 1.5rem 1.5rem;
  }

  .app-dialer-common-content {
    flex: 1 1 100%;
  }
}
</style>
